import { SCALAR_TYPES } from "src/libs/scalars"
import { __, getPlural } from "src/libs/utilities"
import { ALIGN, BACKGROUND_EFFECT_TYPE, BACKGROUND_FILL_TYPE, BORDER_TYPE, DEFAULT_SECTION_PADDING, FONT_CASE, FONT_STYLE, FONT_WEIGHT, LANDING_PRIMITIVE_TYPE, LANDING_TYPE_CONTENT, LINKS_DESIGN_TYPE, OVERFLOW_TYPE, SECTION_DESIGN_TYPE, SECTION_PADDING, SELECT_TYPE, TAB_DESIGN_TYPE, TIMELINE_DIAGRAM_TYPE } from "./interfaces"
import { LandingContentSchema } from "./LandingContentSchema"
import { components } from "./utils/components"

import __background from "../assets/img/background.svg"
import __stack_01 from "../assets/img/background_stack-01.svg"
import __stack_02 from "../assets/img/background_stack-02.svg"
import __card from "../assets/img/card.svg"
import __composition from "../assets/img/composition-icon.svg"
import __css from "../assets/img/css.svg"
import __delimiter from "../assets/img/delimiter.svg"
import __description from "../assets/img/description-1.svg"
import __dot_navidation from "../assets/img/dot_navidation.svg"
import __image from "../assets/img/picture.svg"
import __position from "../assets/img/position.svg"
import __title from "../assets/img/title-1.svg"
import __to_up from "../assets/img/to-up.svg"
import __user from "../assets/img/user.svg"

import { DIALOG_SIZE, POST_STATUS, SC } from "src/libs/interfaces/layouts"
import { NAVIGATION_DESIGN_TYPE, NAVIGATION_POSITION_TYPE, NAVIGATION_SIZE_TYPE, NAVIGATION_THEME_TYPE, NAVIGATION_TYPE } from "src/libs/useful/gallery/Gallery"
import { LANDING_SCALAR } from "./landin_scalars"
import { useLandingStore } from "./store"
import { Position } from "@blueprintjs/core"
import horizontal_tab from "./defaults/horizontal_tab"

export const schema = (): any => {
    const currentLanding = useLandingStore.getState().currentLanding 
    const schemas: any =  {
        Landing :{
            interface: "ILanding",
            visible_value: "title.text",
            name: "Landing",
            plural: "Landings",
            tabs: [
                {
                    _id: "main",
                    title:"Landing"
                },
                {
                    _id: "presets",
                    title: "Presets"
                },
                {
                    _id: "style",
                    title: "Style"
                },
            ],
            apollo_fields: {
                hideOriginalHeader : {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Hide original header on page?",
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "main",
                },
                background: { 
                    kind: "type",
                    type: "external",
                    component: "LandingBackground",
                    title: "Page background", 
                    dataSection: "LandingBackground", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: true,
                    editable: true,
                    tab: "",
                },
                sections: {
                    type: "external_array",
                    kind: "sections",
                    component: "Section",
                    title: "Sections",
                    dataSection: "Section", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    editable: true,
                },
                title:  {
                    kind: "type",
                    type: "Title",
                    component: "Title", 
                    presetLib: "title",
                    icon: __title,
                    title: "Default section's title style",
                    dataSection: "Title", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    isAlwaysOpen: false,
                    tab: "presets",
                },
                description:  {
                    kind: "type",
                    type: "Description",
                    presetLib: "description",
                    icon: __description,
                    component: "Description", 
                    title: "Default section's description style",
                    dataSection: "Description", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    isAlwaysOpen: false,
                    tab: "presets",
                },
                delimiter: {
                    kind: "type",
                    type: "Delimiter",
                    presetLib: "delimiter",
                    component: "Delimiter",
                    title: "Default delimiter",
                    dataSection: "Delimiter", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: true,
                    editable: true,
                    isAlwaysOpen: false,
                    tab: "presets",
                },
                modal: {
                    kind: "type",
                    type: LANDING_TYPE_CONTENT.MODAL,
                    presetLib: "modal",
                    component: "Modal",
                    title: "Default modal",
                    dataSection: "Modal", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: true,
                    editable: true,
                    isAlwaysOpen: false,
                    tab: "presets",
                },
                toUp:  {
                    kind: "type",
                    type: "ToUp", 
                    presetLib: "toUp",
                    icon: __to_up,
                    component: "ToUp",
                    title: "To up button",
                    dataSection: "ToUp", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: true,
                    editable: true,
                    isAlwaysOpen: false,
                    tab: "presets",
                },
                fonts:  {
                    title: "Evailebled Google fonts",
                    commentary: "Select the fonts that will be available in your publication (for example, in headings). Remember, no more than two fonts are needed for a beautiful publication; the more fonts you use, the slower the page will load.",
                    type: SCALAR_TYPES.GOOGLE_FONTS,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                font_size:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Default font size",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    default: 14,
                    tab: "main",
                },
                sectionClass:  {
                    type: SCALAR_TYPES.STRING,
                    title: "Default sections CSS-classes",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                sectionRound:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Default Section's border round",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                gap:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Default Section's gap",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                size:  { 
                    type: SCALAR_TYPES.SELECT, 
                    title: "Landing's container width",
                    values: Object.values( DIALOG_SIZE ).map(v => ({_id: v, title: __( v ),})),
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: DIALOG_SIZE.FULL,
                    tab: "style",
                },
                dataSize:  { 
                    type: SCALAR_TYPES.SELECT, 
                    title: "Default landing's data container width",
                    values: Object.values( DIALOG_SIZE ).map(v => ({_id: v, title: __( v ),})),
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: DIALOG_SIZE.NORMAL,
                    tab: "style",
                },
                defaultPadding: {
                    type: SCALAR_TYPES.SELECT, 
                    title: "Default section's vertical padding",
                    values: Object.values( SECTION_PADDING ).map(v => ({_id: v, title: v,})),
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: DEFAULT_SECTION_PADDING,
                    tab: "style",
                },
                categories:  {
                    type: "external_array", 
                    component: LANDING_SCALAR.CATEGORY,
                    title: "Categories",  
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: true,
                    editable: true,
                    noDefault: true,
                    tab: "presets",
                },
                className:  {
                    type: SCALAR_TYPES.STRING,
                    title: "Landing's CSS classes",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Landing's CSS style",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "style",
                },
                css:  {
                    type: "external_array",
                    kind: "type",
                    title: "Added CSS classes",
                    icon: __css,
                    component: SCALAR_TYPES.STRING,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                isLockAnimation:  {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Lock all create animations",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "main",
                },
            },
            module: "pe-landing-module",
        },
        LandingBackground : {
            interface: "ILandingSectionBackground", 
            name: "Smart background",
            plural: "Smart backgrounds",
            icon: __background,
            tabs: [
                {
                    _id: "main",
                    title: ''
                },
                {
                    _id: "style",
                    title: "Style"
                },
                {
                    _id: "floats",
                    title: "Floats"
                },
                {
                    _id: "behovior",
                    title: "Behovior"
                }
            ],
            apollo_fields: {
                fillType:   {
                    type: SCALAR_TYPES.POPOVER,
                    values:  Object.values(BACKGROUND_FILL_TYPE).map(v => ({ _id: v, title: __( v ), img: `assets/img/landing/bg_${v}.svg`,height: 70, width: 70 })),
                    title: "Background type",
                    default: BACKGROUND_FILL_TYPE.PLAIN,
                    height: 75,
                    width: 75,
                    stepSize: 2,
                    required: false,
                    thread: false,
                    editable: true, 
                    tab: "main",
                },
                image:  {
                    type: SCALAR_TYPES.MEDIA,
                    title: "Image",
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN, BACKGROUND_FILL_TYPE.MADEMOISELLE],
                    thread: true,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                image_opacity:   {
                    type: SCALAR_TYPES.SLIDER,
                    title: "Opacity image",
                    min: 0,
                    max: 1,
                    stepSize: 0.1,
                    labelStepSize: 0.25,
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN, BACKGROUND_FILL_TYPE.MADEMOISELLE],
                    thread: true,
                    default: 1,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                image_mask:   {
                    type: SCALAR_TYPES.STRING,
                    title: "SOME: image mask",
                    required: false,
                    thread: false,
                    noDefault: true,
                    editable: true,
                },
                tile_opacity:   {
                    type: SCALAR_TYPES.SLIDER,
                    title: "Image tile opacity", 
                    min: 0,
                    max: 1,
                    stepSize: 0.1,
                    labelStepSize: 0.25,
                    default: 1,
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN, BACKGROUND_FILL_TYPE.MADEMOISELLE],
                    thread: false,
                    noDefault: true,
                    editable: true, 
                }, 
                tile_size_px:   {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Image tile size (px)", 
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                tile_position_x: {
                    type: SCALAR_TYPES.CSS_MAGNITUDE,
                    title: "Image tile x position", 
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                tile_position_y:  {
                    type: SCALAR_TYPES.CSS_MAGNITUDE,
                    title: "Image tile y position", 
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                tile_repeat:  {
                    type: SCALAR_TYPES.TAGS,
                    title: "Repeat", 
                    values: [
                        {
                            _id:"repeat",
                            title: "Repeat"
                            
                        },
                        {
                            _id:"no-repeat",
                            title: "No repeat"
                        }
                    ],
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                stack: {
                    type: SCALAR_TYPES.IMAGE_RADIO, 
                    values: [
                        {
                            _id: "image",
                            title: "Image",
                            img: __stack_01,
                            height: 55,
                        }, 
                        {
                            _id: SCALAR_TYPES.COLOR,
                            title: SCALAR_TYPES.COLOR,
                            img: __stack_02,
                            height: 55,
                        },
                    ],
                    default: SCALAR_TYPES.COLOR,
                    title: "What is forward?",
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN, BACKGROUND_FILL_TYPE.MADEMOISELLE],
                    thread: true,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                color: {
                    type: SCALAR_TYPES.COLOR,
                    title: "Сolor",
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN, BACKGROUND_FILL_TYPE.MADEMOISELLE, BACKGROUND_FILL_TYPE.MADEMOISELLE],
                    thread: true,
                    editable: true,
                    tab: "main",
                },  
                color2: {
                    type: SCALAR_TYPES.COLOR,
                    title: "Сolor 2",
                    required: ["fillType",  BACKGROUND_FILL_TYPE.HEXAGONS],
                    thread: true,
                    editable: true,
                    tab: "main",
                },  
                border: {
                    type: SCALAR_TYPES.BORDER,
                    subtitle: "Background's border",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true,
                    tab: "style",
                },  
                floats:   {
                    type: "external_array",
                    kind: "type",
                    dataSection: "floats",
                    dataName: "title",
                    component: "Float",
                    visualization: "landingCompositeDataType",
                    title: "Floats", 
                    required: true,
                    thread: false,
                    editable: true,
                    tab: "floats",
                }, 
                opacity:  {
                    type: SCALAR_TYPES.SLIDER,
                    title: "Color opacity",
                    min: 0,
                    max: 1,
                    stepSize: 0.1,
                    labelStepSize: 0.25,
                    default: 1,
                    required: ["fillType", BACKGROUND_FILL_TYPE.PLAIN, BACKGROUND_FILL_TYPE.MADEMOISELLE],
                    thread: true,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                dark:   {
                    type: SCALAR_TYPES.COLOR,
                    subtitle: "SOME: dark",
                    required: [ "fillType", BACKGROUND_FILL_TYPE.MADEMOISELLE ],
                    thread: false,
                    noDefault: true,
                    editable: true,
                }, 
                color_rotate:   {// 0-360 deg
                    type: SCALAR_TYPES.SLIDER,
                    min:0,
                    max: 360,
                    labelStepSize: 45,
                    stepSize: 1,
                    title: "Medemiselle rotating",
                    required: [ "fillType", BACKGROUND_FILL_TYPE.MADEMOISELLE ],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                color_contrast:   {// 0-100 for mademoiselle
                    type: SCALAR_TYPES.SLIDER,
                    min:0,
                    max: 100,
                    title: "Medemiselle contrast",
                    required: [ "fillType", BACKGROUND_FILL_TYPE.MADEMOISELLE ],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                color_size:   {// size for mademoiselle
                    type: SCALAR_TYPES.NUMBER,
                    title: "Medemiselle color size",
                    required: [ "fillType", BACKGROUND_FILL_TYPE.MADEMOISELLE ], 
                    postfix: "px",
                    min: 0,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                effect:   {
                    type: SCALAR_TYPES.TAGS,
                    values:  Object.values(BACKGROUND_EFFECT_TYPE).map(v => ({_id: v, title: __( v )})), 
                    title: "Background behovior",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "behovior",
                },
                parallax_speed:   {
                    type: SCALAR_TYPES.SLIDER,
                    title: "Parallax speed",
                    required: ["effect", BACKGROUND_EFFECT_TYPE.VERTICAL_PARALLAX],
                    default: -0.1,
                    min: -0.3,
                    max: 0.3,
                    stepSize: 0.1,
                    labelSize: 0.025,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "behovior",
                },
                parallax_y_offset:   {
                    type: SCALAR_TYPES.SLIDER,
                    title: "Parallax y-offset",
                    required: ["effect", BACKGROUND_EFFECT_TYPE.VERTICAL_PARALLAX],
                    default: 0,
                    min: -300,
                    max: 300,
                    stepSize: 50,
                    labelSize: 50,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "behovior",
                },
                style:   {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Background's CSS style",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "style",
                },  
            },
            module: "pe-landing-module",
        },
        Title : {
            interface: "ILandingTitleProps",
            visible_value: "text",
            name: "Title",
            plural: "Title",
            icon: __title,
            libName: "title",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "typo",
                    title: "Typographics",
                },
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: { 
                text: {
                    type: SCALAR_TYPES.TEXT,
                    subtitle: "Text", 
                    required: false,
                    thread: false,
                    editable: true,
                    rows: 8,
                    tab: "main",
                },   
                className: {
                    type: SCALAR_TYPES.STRING,
                    subtitle: "CSS classes",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                Typographics: {
                    type: LANDING_SCALAR.TYPOGRAPHY,
                    component: "Typographics",
                    title: "Typographics", 
                    dataSection: "Typographics", 
                    dataName: "position",
                    visualization: "landingCompositeDataType",
                    required: false,
                    isDefaultOpen:true, 
                    isNotCard:true,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "typo",
                },
                style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    icon: __css,
                    title: "CSS style",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "style",
                },
                container_class_name: {
                    type: SCALAR_TYPES.STRING,
                    subtitle: "Container CSS classes",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                animated: {
                    type: LANDING_SCALAR.ANIMATED, 
                    title: "Animation", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "behovior",
                },
                position: {
                    type: "Position",
                    dataSection: "Section",
                    dataName: "position",  
                    visualization: "landingCompositeDataType",
                    isNotCard: true,
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "position",
                },
            },
            module: "pe-landing-module",
        },
        Description : {
            interface: "ILandingTitleProps",
            visible_value: "text",
            name: "Description",
            plural: "Descriptions",
            icon: __description,
            libName: "description",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "typo",
                    title: "Typographics",
                },
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                text: {
                    type: SCALAR_TYPES.TEXT,
                    subtitle: "text", 
                    required: false,
                    thread: false,
                    editable: true,
                    rows: 8, 
                    tab: "main",
                },  
                Typographics: {
                    type: LANDING_SCALAR.Typographics,
                    component: "Typographics",
                    title: "Typographics", 
                    dataSection: "Typographics", 
                    dataName: "position",
                    visualization: "landingCompositeDataType",
                    required: false,
                    isDefaultOpen:true, 
                    isNotCard:true,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "typo",
                }, 
                className: {
                    type: SCALAR_TYPES.STRING,
                    subtitle: "CSS classes",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "CSS style",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },   
                animated: {
                    type: LANDING_SCALAR.ANIMATED, 
                    title: "Animation", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "behovior",
                },
                container_class_name: {
                    type: SCALAR_TYPES.STRING,
                    subtitle: "Container CSS classes",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },             
                position: {
                    type: "Position", 
                    dataSection: "Section",
                    dataName: "position", 
                    visualization: "landingCompositeDataType",
                    isNotCard: true,
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "position",
                },
            },
            module: "pe-landing-module",
        },
        Float: {
            interface: "ILandingFloat", 
            name: "Float",
            title: "Float",
            plural: "Floats",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {  
                primitives: {
                    type: "external_array",
                    kind: "type",
                    component: "Primitive",
                    // title: "Primitives",
                    dataSection: "primitives",
                    dataName: "primitive", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    isAlwaysOpen: true,
                    isNotCard: false,
                    //isDefaultOpen: true,
                    editable: true,
                    tab: "main",
                },
                text: {
                    type: SCALAR_TYPES.TEXT,
                    title: "Text", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "no",
                },   
                isHidden:  {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Hidden", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "behovior",
                },  
                overflow: { 
                    type: SCALAR_TYPES.TAGS,
                    values:  Object.values(OVERFLOW_TYPE).map(v => ({_id: v, title: __( v )})),
                    title: "Overflow type", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "behovior",
                },  
                //effect?: BACKGROUND_EFFECT_TYPE
                //parallax_speed?: number
                //parallax_y_offset?: number
                //className?: string
                //style?: CSSProperties
                animated: {
                    type: LANDING_SCALAR.ANIMATED, 
                    title: "Animation", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "behovior",
                }, 
                position: {
                    type: "Position",
                    dataSection: "Section",
                    dataName: "position", 
                    visualization: "landingCompositeDataType",
                    disabled_fields: [ "width", "height", "opacity" ],
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "position",
                },
                style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Container CSS style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: false, 
                    tab: "style", 
                },
                id: {
                    type: SCALAR_TYPES.ID,
                    title: "id", 
                    required: false,
                    thread: false,
                    editable: false,
                    tab: "main",
                },
            },
            module: "pe-landing-module",
        },
        Primitive: {
            interface: "ILandingPrimitive", 
            name: "Primitive",
            plural: "Primitives",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                type:  {
                    type: SCALAR_TYPES.IMAGE_RADIO,
                    values: [ 
                        ...Object.keys( LANDING_PRIMITIVE_TYPE ), 
                        // ...Object.keys( LANDING_TYPE_CONTENT ) 
                    ].map((key: string) => {
                        const val = LANDING_PRIMITIVE_TYPE[ key as typeof LANDING_PRIMITIVE_TYPE ].toString()
                        return {
                            _id: val,
                            title: val,
                            img: `/assets/img/landing/${ val }.svg`,
                            height: 45,
                        }
                    }),
                    title: "Type",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //LANDING_PRIMITIVE_TYPE | LANDING_TYPE_CONTENT | string
                data: {
                    
                }, //? ILandingPrimitive

                fill : {
                    type: SCALAR_TYPES.COLOR,
                    title: "fill color",
                    required: ["type", ...Object.values( LANDING_PRIMITIVE_TYPE )],
                    thread: true,
                    editable: true,
                    // onlyPlainColor: true,
                    tab: "main",
                },
                stroke: {
                    type: SCALAR_TYPES.COLOR,
                    title: "border color",
                    required: ["type", ...Object.values( LANDING_PRIMITIVE_TYPE )],
                    thread: true,
                    editable: true,
                    // onlyPlainColor: true,
                    tab: "main",
                },
                strokeWidth: {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "Border width",
                    required: ["type", ...Object.values( LANDING_PRIMITIVE_TYPE )],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                strokeDasharray: {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "Dash width",
                    required: ["type", ...Object.values( LANDING_PRIMITIVE_TYPE )],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                strokeDashoffset: {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "Dash gap width",
                    required: ["type", ...Object.values( LANDING_PRIMITIVE_TYPE )],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },

                bg: {
                    type: SCALAR_TYPES.COLOR,
                    title: "Background color",
                    required: false,
                    thread: true,
                    editable: true,
                    // onlyPlainColor: true,
                    noDefault: true,
                    tab: "style",
                },
                isHidden:  {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Hidden", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "behovior",

                }, 
                // isInline?://boolean
                // effect?: //BACKGROUND_EFFECT_TYPE
                // parallax_speed?: //number
                // parallax_y_offset?: //number
                // className?: //string
                // height?://number | string
                animated: {
                    type: LANDING_SCALAR.ANIMATED, 
                    title: "Animation", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "behovior",
                }, 
                position: {
                    type: "Position", 
                    dataSection: "Section",
                    dataName: "position", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "position",
                }, 
                id: {
                    type: SCALAR_TYPES.ID,
                    title: "id", 
                    required: false,
                    thread: false,
                    editable: false,
                    noDefault: true,
                    tab: "main-1",
                },
                style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Container CSS style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
            },
            module: "pe-landing-module",
        },
        Delimiter : {
            interface: "ILandingDelimiter", 
            icon: __delimiter,
            name: "Delimiter",
            plural: "Delimiters",
            libName: "delimiter",
            tabs: [
                {
                    _id: "main",
                    title:"Landing"
                }, 
            ],
            apollo_fields: {
                isDisabled: {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Hidden", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    default: false,
                    tab: "main",
                },
                width: {
                    type: SCALAR_TYPES.CSS_MAGNITUDE,
                    title: "Width", 
                    //required: ["isDisabled", false, undefined],
                    thread: false,
                    editable: true,
                    noDefault: false, 
                    default: "100%",
                    tab: "main",
                },
                height: {
                    type: SCALAR_TYPES.CSS_MAGNITUDE,
                    title: "Height", 
                    //required: ["isDisabled", false, undefined],
                    thread: false,
                    editable: true,
                    noDefault: false,
                    default: 1,
                    tab: "main",
                },
                x: {
                    type: SCALAR_TYPES.CSS_MAGNITUDE,
                    title: "Left shift", 
                    //required: ["isDisabled", false, undefined],
                    thread: false,
                    editable: true,
                    noDefault: true, 
                    default: 0,
                    tab: "main",
                },
                y: {
                    type: SCALAR_TYPES.CSS_MAGNITUDE,
                    title: "Top shift", 
                    //required: ["isDisabled", false, undefined],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: 0,
                    tab: "main",
                },
                bottom: {
                    type: SCALAR_TYPES.CSS_MAGNITUDE,
                    title: "Bottom shift", 
                    //required: ["isDisabled", false, undefined],
                    thread: false,
                    editable: true,
                    noDefault: true, 
                    default: 0,
                    tab: "main",
                },
                className: {
                    type: SCALAR_TYPES.STRING,
                    title: "CSS classes",
                    //required: ["isDisabled", false, undefined],
                    thread: false,
                    editable: true,
                    noDefault: true, 
                    default: "",
                    tab: "main",
                },
                style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "CSS style",
                    icon: __css, 
                    //required: ["isDisabled", false, undefined],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                //element?: // JSX.Element,
                bg: {
                    type: SCALAR_TYPES.COLOR,
                    title: "fill color",
                    //required: ["isDisabled", false, undefined],
                    thread: true,
                    editable: true, 
                    noDefault: false,
                    default: "0x000000",
                    tab: "main",
                },
                edges: {
                    type: "external_array",
                    kind: "sections",
                    component: "Edge",
                    title: "Bottom Edge",
                    subtitle: "Bottom Edge",
                    dataSection: "Bottom Edge", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "main",
                }
            },
            module: "pe-landing-module",
        },
        Edge: {
            interface: "ILandingDelimiterEdge", 
            icon: __delimiter,
            name: "Edge",
            plural: "Edges",
            tabs: [
                {
                    _id: "main",
                    title:"Edge"
                },
                {
                    _id: "style",
                    title: "Stylize"
                }, 
                {
                    _id: "position",
                    title: "Position"
                }, 
            ],
            apollo_fields: {
                type: {
                    type: SCALAR_TYPES.POPOVER,
                    values:  [ 1,2,3,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,30, ].map(v => ({
                        _id: v, 
                        title: "Edge" + v, 
                        img: `../views/utils/edge/Edge${v}.svg`, 
                        height: 75, 
                    }) ),
                    title: "Edge type", 
                    subtitle: "Edge type", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true, 
                    tab: "main",
                },
                fill: {
                    type: SCALAR_TYPES.COLOR,
                    title: "Color", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }
            }
        },
        Composition : {
            interface: "ISectionComposition",
            visible_value: "columns",
            name: "Composition",
            plural: "Compositions",
            icon: __composition,
            tabs: [
                {
                    _id: "main",
                    title: "Composition"
                },
            ],
            apollo_fields: {
                columns: {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "Columns", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, 
                proportia: {  
                    type: LANDING_SCALAR.PROPORTION, 
                    subtitle: "Proportia", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: false,
                    tab: "main",
                },
                isBlocked: {
                    type: SCALAR_TYPES.BOOLEAN,
                    subtitle: "is Blocked", 
                    isHidden: true,
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main--",
                }, 
                container: {                    
                    type: SCALAR_TYPES.SELECT,
                    values: Object.values( DIALOG_SIZE ),
                    subtitle: "Landing's container width",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                
                width: {
                    type: SCALAR_TYPES.NUMBER,
                    isHidden: true,
                    subtitle: "width", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "meats",
                },
                textBefore: {
                    type: SCALAR_TYPES.TEXT,
                    subtitle: "text Before", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "meats",
                }, 
                textAfter: {
                    type: SCALAR_TYPES.TEXT,
                    subtitle: "text After", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "meats",
                },  
            },
            module: "pe-landing-module",
        },
        User : {
            interface: "IUser", 
            name: "User",
            plural: "Users",
            icon: __user,
            tabs: [
                {
                    _id: "main",
                    title: "Position"
                },
                {
                    _id: "style",
                    title: "Stylize"
                },
            ],
            apollo_fields: {
                avatar:    {
                    type: SCALAR_TYPES.MEDIA,
                    title: "Avatar", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, 
                first_name: {
                    type: SCALAR_TYPES.STRING,
                    title: "First name", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                last_name:     {
                    type: SCALAR_TYPES.STRING,
                    title: "Last name", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                display_name:     {
                    type: SCALAR_TYPES.STRING,
                    title: "Display name", 
                    commentary: "Alternative for first name and last name",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                description:     {
                    type: SCALAR_TYPES.HTML,
                    title: "Description", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                
                className: {
                    type: SCALAR_TYPES.STRING,
                    title: "CSS class", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "CSS Style of data content", 
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
            },
            module: "pe-landing-module",
        },
        Position : {
            interface: "IScreenPosition", 
            name: "Position",
            plural: "Positions",
            icon: __position,
            tabs: [
                {
                    _id: "main",
                    title: "Position"
                }, 
            ],
            apollo_fields: {
                left:    {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "By left", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                top:     {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "By top", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                width:   {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "Width", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                height:  {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "Height", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                rotate:  {
                    type: SCALAR_TYPES.SLIDER,
                    subtitle: "Rotate", 
                    min: -180,
                    max: 180,
                    stepSize: 1,
                    labelStepSize: 45,
                    disabledLabel: false,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                scale:  {
                    type: SCALAR_TYPES.SLIDER,
                    subtitle: "Scale", 
                    min: 0,
                    max: 400,
                    stepSize: 1,
                    labelStepSize: 50, 
                    disabledLabel: false,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                z:     {
                    type: SCALAR_TYPES.SLIDER,
                    subtitle: "Z-index", 
                    min:0,
                    max: 100,
                    stepSize: 1,
                    labelStepSize: 25,
                    disabledLabel: false,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                opacity : {
                    type: SCALAR_TYPES.SLIDER,
                    subtitle: "Opacity",
                    min: 0,
                    max: 1,
                    stepSize: 0.1,
                    labelStepSize: 0.25,
                    default: 1,
                    required: false,
                    thread: true,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
            },
            module: "pe-landing-module",
        },
        Section: {
            interface: "ILandingSection",
            tabs: [
                {
                    _id: "main",
                    title: "Section",
                },
                {
                    _id: "meats",
                    title: "Meat",
                    description: "Section content. May include one or more different types of elements. Organize content using different layout types (see first tab)."
                },
                {
                    _id: "title",
                    title: "Title",
                    description: "",
                }, 
                // {
                //     _id: "composition",
                //     title: "Composition",
                //     description: "Manage section layout (size, number of columns, section styles, etc.)",
                // },
                {
                    _id: "background",
                    title: "Smart background",
                    description: "",

                },
                {
                    _id: "delimiter",
                    title: "Delimiter", 
                    description: "",
                },
                {
                    _id: "floats",
                    title: "Floats",
                    latent: true,
                    description: "Floats are content elements that sit on top of a section's main content. They can be freely rotated, adjusted, and resized.", 
                },
                {
                    _id: "layout",
                    title: "Layout",
                    latent: true,
                },
            ],
            visible_value: "title.text",
            name: "Section",
            plural: "Sections",
            apollo_fields: {
                data:   {
                    type: "external_array",
                    kind: "type",
                    dataSection: "data",
                    dataName: "title",
                    component: "LandingContent",
                    visualization: "landingCompositeDataType",
                    title: "Meats", 
                    required: true,
                    isDeleted: true,
                    thread: false,
                    editable: true,
                    tab: "meats",
                }, //ILandingContent[]
                
                title:   {
                    type: "Title", 
                    dataSection: "Section",
                    dataName: "title",
                    title: "Title", 
                    isDefaultOpen: true,
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    editable: true,
                    tab: "title",
                }, //ILandingSectionTitle
                description: {
                    type: "Description", 
                    dataSection: "Section",
                    dataName: "description",
                    title: "Description", 
                    visualization: "landingCompositeDataType", 
                    required: true,
                    thread: false,
                    editable: true,
                    tab: "title",
                }, //ILandingSectionDescription
                delimiter: {
                    type: "Delimiter",
                    title: "Delimiter", 
                    visualization: "landingCompositeDataType",
                    isDefaultOpen: true,
                    isNotCard:true,
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "delimiter",
                }, //ILandingDelimiter 
                height: {
                    type: SCALAR_TYPES.CSS_MAGNITUDE,
                    title: "Height", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, //number | string
                width: {
                    type: SCALAR_TYPES.CSS_MAGNITUDE,
                    title: "Width", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, //number | string
                overflow: { 
                    type: SCALAR_TYPES.TAGS,
                    values:  Object.values(OVERFLOW_TYPE).map(v => ({_id: v, title: __( v )})),
                    title: "Overflow type", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: OVERFLOW_TYPE.HIDDEN,
                    tab: "main",
                }, //OVERFLOW_TYPE
                designType: {
                    type: SCALAR_TYPES.POPOVER,
                    values:  Object.values(SECTION_DESIGN_TYPE).map(v => (
                        {
                            _id: v, 
                            title: __( v ), 
                            description: __(`${ v } description`),
                            img: `/assets/img/landing/design_type_${v}.svg`, 
                            height: 75,
                            width: 75,
                        }
                    )),
                    height: 25,
                    width: 150,
                    stepSize: 2,
                    title: "Design type", 
                    commentary: "If there is more than one content element (second tab), they can be formatted in several ways.",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    default: SECTION_DESIGN_TYPE.FEED,
                    tab: "main",
                }, //SECTION_DESIGN_TYPE
                horizontal_align: {
                    type: SCALAR_TYPES.SELECT,
                    values: Object.values(ALIGN).map(key => ({
                        _id: key,
                        title: key
                    })),
                    title: "Horizontal align", 
                    isHidden: false,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: ALIGN.FULL,
                    tab: "main",
                }, //ALIGN
                vertical_align:{
                    type: SCALAR_TYPES.SELECT,
                    values: Object.values(ALIGN).map(key => ({
                        _id: key,
                        title: key
                    })),
                    title: "Vertical align", 
                    isHidden: false,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: ALIGN.MIDDLE,
                    tab: "main",
                }, //ALIGN
                floats: {
                    type: "external_array",
                    kind: "type", 
                    dataSection: "floats",
                    dataName: "title",
                    component: "Float",
                    visualization: "landingCompositeDataType",
                    title: "Float", 
                    required: true,
                    thread: false,
                    editable: true,
                    tab: "floats",
                }, 
                
                paddingTop: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Padding top",  
                    thread: false,
                    editable: true,
                    noDefault: true, 
                    default: SECTION_PADDING.NORMAL,
                    tab: "main",
                }, //SECTION_PADDING
                paddingBottom: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Padding bottom",  
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: SECTION_PADDING.NORMAL,
                    tab: "main",
                }, //SECTION_PADDING
                background: {
                    type: "LandingBackground",
                    dataSection: "Section",
                    dataName: "background",
                    title: "Smart background", 
                    visualization: "landingCompositeDataType", 
                    isDefaultOpen: true,
                    required: true,
                    thread: false,
                    editable: true,
                    tab: "background",
                }, //ILandingSectionBackground
                
                composition: {
                    type: "Composition", 
                    title: "Composition", 
                    icon: __composition,
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                position: {
                    type: "Position", 
                    dataSection: "Section",
                    dataName: "position", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "composition",
                }, //IScreenPosition
                dimentions: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "dimentions", 
                    visualization: "landingDimention",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "top",
                }, //IScreenModes
                dstyle: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "CSS style of Section container", 
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "composition",
                }, //CSSProperties
                itemStyle: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "CSS style of every carousel item container", 
                    icon: __css,
                    required: [ "designType", SECTION_DESIGN_TYPE.CAROUSEL ],
                    thread: false,
                    editable: true,
                    tab: "composition",
                }, //CSSProperties
                animated: {
                    type: LANDING_SCALAR.ANIMATED, 
                    title: "Animation", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },

                /** SLIDER */
                is_navigation : {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Slider pagination", 
                    required: [ "designType", SECTION_DESIGN_TYPE.CAROUSEL ],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "layout",
                },
                carousel_auto_height : {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Carousel auto height by slides", 
                    required: [ "designType", SECTION_DESIGN_TYPE.CAROUSEL ],
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "layout",
                },
                carousel_nav_type: {
                    type: SCALAR_TYPES.IMAGE_RADIO,
                    title: "Carousel navigation button icons",
                    values : Object.values( NAVIGATION_DESIGN_TYPE ).map(v => ({ _id:v, img: `/assets/img/landing/${ v }.svg`, height: 20, padding: 12 })),
                    required: ["designType", SECTION_DESIGN_TYPE.CAROUSEL], 
                    thread: false,
                    editable: true,
                    noDefault: false,
                    default: NAVIGATION_DESIGN_TYPE.CHEVRON,
                    tab: "layout",
                }, 
                carousel_nav_position: {
                    type: SCALAR_TYPES.IMAGE_RADIO,
                    title: "Vertical position of carousel navigation buttons",
                    values : Object.values( NAVIGATION_POSITION_TYPE ).map((v, i) => ({ _id:v, img: `/assets/img/landing/carousel-nav-0${ i + 1 }.svg`, height: 100 })),
                    required: ["designType", SECTION_DESIGN_TYPE.CAROUSEL], 
                    thread: false,
                    editable: true,
                    noDefault: false,
                    default: NAVIGATION_POSITION_TYPE.TOP_LEFT,
                    tab: "layout",
                }, 
                carousel_nav_size: {
                    type: SCALAR_TYPES.TAGS,
                    title: "Size of carousel navigation buttons",
                    values : Object.values( NAVIGATION_SIZE_TYPE ).map(v => ({ _id:v, title: __( v ) })),
                    required: ["designType", SECTION_DESIGN_TYPE.CAROUSEL], 
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "layout",
                }, 
                carousel_nav_theme: {
                    type: SCALAR_TYPES.TAGS,
                    title: "Carousel navigation theme",
                    values : Object.values( NAVIGATION_THEME_TYPE ).map(v => ({ _id:v, title: __( v ) })),
                    required: ["designType", SECTION_DESIGN_TYPE.CAROUSEL], 
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "layout",
                }, 
                carousel_nav: {
                    type: SCALAR_TYPES.TAGS,
                    title: "Carousel navigation buttons form",
                    values : Object.values( NAVIGATION_TYPE ).map(v => ({ _id:v, title: __( v ) })),
                    required: ["designType", SECTION_DESIGN_TYPE.CAROUSEL], 
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "layout",
                }, 
                carousel_autoplay:  {
                    type: SCALAR_TYPES.TAGS,
                    title: "Carousel autoplay duration", 
                    values: [
                        {
                            _id: 0,
                            title: "none"
                        },
                        {
                            _id: 1000,
                            title: "1 sec"
                        },
                        {
                            _id: 2000,
                            title: "2 sec"
                        },
                        {
                            _id: 4000,
                            title: "4 sec"
                        },
                        {
                            _id: 5000,
                            title: "5 sec"
                        },
                        {
                            _id: 7000,
                            title: "7 sec"
                        },
                        {
                            _id: 10000,
                            title: "10 sec"
                        },
                        {
                            _id: 15000,
                            title: "15 sec"
                        },
                        {
                            _id: 30000,
                            title: "30 sec"
                        },
                    ],
                    required: ["feedType", SECTION_DESIGN_TYPE.CAROUSEL], 
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "layout",
                }, 
                /**Tab design */
                tabs_design_type: {
                    type: SCALAR_TYPES.SELECT,
                    title: "Tabs design type",
                    values : Object.values( TAB_DESIGN_TYPE ).map( (v, i) => ({ _id:v, title: horizontal_tab()[i]?.title || v }) ),
                    required: ["designType", SECTION_DESIGN_TYPE.TABS], 
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "layout",
                }, 
                tab_design: { 
                    type: "TabDesignStyle",
                    component: "TabDesignStyle",
                    title: "Tab design", 
                    dataSection: "TabDesignStyle", 
                    visualization: "landingCompositeDataType",
                    required: ["designType", SECTION_DESIGN_TYPE.TABS], 
                    thread: true,
                    editable: true,
                    isAlwaysOpen: false,
                    isCollapsesOpen: false,
                    tab: "layout",
                },
                /** */
                haccordeon_design: {
                    type: "HAccordeonStyle",
                    component: "HAccordeonStyle",
                    title: "Horizontal accordeon design", 
                    dataSection: "HAccordeonStyle", 
                    visualization: "landingCompositeDataType",
                    required: ["designType", SECTION_DESIGN_TYPE.HACCORDEON, SECTION_DESIGN_TYPE.ACCORDEON], 
                    thread: true,
                    editable: true,
                    isAlwaysOpen: true,
                    isCollapsesOpen: true,
                    tab: "layout",
                },
                /** */
                dots: {
                    type:"TimelineDot",
                    component: "TimelineDot",
                    title: "Timeline design", 
                    dataSection: "TimelineDot", 
                    visualization: "landingCompositeDataType",
                    required: [ "designType", SECTION_DESIGN_TYPE.VTIMELINE, SECTION_DESIGN_TYPE.HTIMELINE ], 
                    thread: true,
                    editable: true,
                    isAlwaysOpen: true,
                    isCollapsesOpen: true,
                    tab: "layout",
                },

                isHidden:  {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Hidden", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",

                }, //boolean
                id: {
                    type: SCALAR_TYPES.ID,
                    title: "id", 
                    required: false,
                    thread: false,
                    editable: false,
                    tab: "main",
                }, // 
            },
            module: "pe-landing-module",
        }, 
        [ LANDING_SCALAR.CATEGORY] : {
            interface: "ILandingCategory",
            name: "Category",
            plural: "Categories",
            apollo_fields: {
                id: {
                    type: SCALAR_TYPES.ID,
                    title: "ID", 
                    required: false,
                    thread: false,
                    editable: true, 
                    tab: "main",
                },
                title: {
                    type: SCALAR_TYPES.TITLE,
                    title: "Title", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                content: {
                    type: SCALAR_TYPES.HTML,
                    title: "Content", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                parentId: {
                    type: LANDING_SCALAR.LANDING_MEMBER_ID,
                    visualization: "landingCompositeDataType",
                    subtitle: "Parent", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                color: {
                    type: SCALAR_TYPES.COLOR,
                    title: "Color", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
            },
            visible_value:"title",
            module: "pe-landing-module",
        },
        ToUp:{
            interface: "IToUp", 
            name: "To up button",
            plural: "To up buttons",
            icon: __to_up,
            tabs: [
                {
                    _id: "main",
                    title: "To up button"
                },
                {
                    _id: "position",
                    title: "Position"
                },
            ],
            apollo_fields: { 
                design: {
                    type: SCALAR_TYPES.IMAGE_RADIO,
                    values:  Object.values( NAVIGATION_DESIGN_TYPE ).map(v => (
                        {
                            _id: v, 
                            title: __( v ), 
                            img: `/assets/img/landing/${v}.svg`, 
                            height: 50
                        }
                    )),
                    style: {
                        maxWidth: 50
                    },
                    subtitle: "Design type", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //string | NAVIGATION_DESIGN_TYPE
                className:  {
                    type: SCALAR_TYPES.STRING,
                    subtitle: "CSS classes",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "style",
                },
                style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "CSS properties",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "style",
                }, 
                text_color:  {
                    type: SCALAR_TYPES.COLOR,
                    title: "Color", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: "var(--force-contast-color)",
                    tab: "main",

                }, 
                font_size:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Size", 
                    required: false,
                    thread: false,
                    editable: true,
                    default: 20,
                    tab: "main",

                }, 
                place: {
                    type: LANDING_SCALAR.SCREEN_PLACE,
                    subtitle: "Screen Place", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    default: Position.LEFT_BOTTOM,
                    tab: "position",
                },
                margin:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Indent from the edge of the screen", 
                    required: false,
                    thread: false,
                    editable: true,
                    default: 30,
                    tab: "main",

                },                 
                border: {
                    type: SCALAR_TYPES.BORDER,
                    subtitle: "Border",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true,
                    tab: "main",
                },
                isHidden:  {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Do hide", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",

                }, 
                position: {
                    type: "Position", 
                    dataSection: "Section",
                    dataName: "position", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "position",
                }, 
            },
            module: "pe-landing-module",
        },
        LandingContent:{
            interface: "ILandingContent", 
            name: "Meat",
            plural: "Meats",
            parentType: "",
            tabs: getLandingContentTabs(),
            apollo_fields: { 
                type:  {
                    type: SCALAR_TYPES.SELECT,
                    values: Object.keys( components() ),
                    title: "Type",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "boo",
                },
                className:  {
                    type: SCALAR_TYPES.STRING,
                    title: "CSS classes",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                // height:  {
                //     type: SCALAR_TYPES.CSS_MAGNITUDE,
                //     title: "Height",
                //     required: false,
                //     thread: false,
                //     editable: true,
                //     noDefault: true,
                //     tab: "main",
                // },
                // width:  {
                //     type: SCALAR_TYPES.CSS_MAGNITUDE,
                //     title: "Width",
                //     required: false,
                //     thread: false,
                //     editable: true,
                //     noDefault: true,
                //     default: "100%",
                //     tab: "main",
                // },
                style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Meat CSS properties",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                containerStyle: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Container's CSS properties",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                horizontal_align: {
                    type: SCALAR_TYPES.SELECT,
                    values: Object.values(ALIGN),
                    title: "Horizontal align of Meat in section", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                vertical_align:{
                    type: SCALAR_TYPES.SELECT,
                    values: Object.values(ALIGN),
                    title: "Vertical align of Meat in section", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                }, 
                animated: {
                    type: LANDING_SCALAR.ANIMATED, 
                    title: "Animation when appearing on screen", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "behovior",
                },
                position: {
                    type: "Position",
                    dataSection: "Section",
                    dataName: "position", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "position",
                }, 
                 
                smart_background: { 
                    type: "LandingBackground",
                    dataSection: "Section",
                    dataName: "background",
                    //component: "LandingBackground",
                    title: "Smart background",
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: true,
                    editable: true,
                    tab: "background",
                },

                id:  {
                    type: SCALAR_TYPES.ID, 
                    title: "id",
                    required: false,
                    thread: false,
                    editable: false,
                    noDefault: true,
                    tab: "id",
                },
                text_color: {
                    type: SCALAR_TYPES.COLOR,
                    title: "Text color", 
                    description: "",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "typo", 
                }, //?:Color
                font: {
                    title: "Font",
                    type: SCALAR_TYPES.TAGS,
                    values: currentLanding.fonts.map((font: string) => ({_id: font, title: font})), 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    default: currentLanding.fonts[0],
                    tab: "typo", 
                },                
                text_align: {
                    type: SCALAR_TYPES.IMAGE_RADIO,
                    title: "Text align", 
                    values: Object.values(ALIGN).map(key => ({
                        _id: key,
                        img: `/assets/img/landing/align-${key}.svg`,
                        height: 25,
                        title: undefined
                    })),
                    required: false,
                    //parent:"text",
                    thread: false,
                    editable: true,
                    default: ALIGN.START,
                    noDefault: false,
                    tab: "typo",
                },
                font_weight: {
                    type: SCALAR_TYPES.TAGS,
                    values: Object.keys(FONT_WEIGHT).map((key: string) => ({_id: key, title:__( key ), style:{ fontWeight: Number( FONT_WEIGHT[key].toString() ) }})),
                    title: "Font weight", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "typo",
                }, //?: FONT_WEIGHT
                text_size: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Font size",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    default: 14,
                    tab: "typo",
                }, //?: number
                font_case: {
                    type: SCALAR_TYPES.TAGS,
                    values: Object.values(FONT_CASE).map(key => ({ _id:key, title:__( key ), style:{ textTransform: key } })),
                    title: "Font case", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "typo",
                }, //: FONT_CASE
                font_style: {
                    type: SCALAR_TYPES.TAGS,
                    values: Object.values(FONT_STYLE).map(key => ({ _id:key, title:__( key ), style:{ textTransform: key } })),
                    title: "Font style", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "typo",
                }, 
                line_height: {
                    type: SCALAR_TYPES.SLIDER, 
                    title: "Line height",
                    min:0,
                    max:5,
                    stepSize: 0.1,
                    labelStepSize: 2.5, 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "typo",
                },
                letter_spacing: {
                    type: SCALAR_TYPES.SLIDER, 
                    title: "Letter spacing",
                    min:0,
                    max:10,
                    stepSize:1,
                    labelStepSize: 5, 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "typo",
                },
            },
            module: "pe-landing-module",
        },
        ...{
            ...(Object.keys( components())).reduce((a, v) => {
                const tabs = LandingContentSchema()[ v ]?.tabs || []
                const data =  {                     
                    [v]: getSingleLandingContentSchema( v ),
                    ...a, 
                } 
                return data
            }, {})  
        }, 

        
        [ LANDING_TYPE_CONTENT.BUTTON ]: {

            interface: "ButtonContent", 
            name: "Button",
            plural: "Buttons",
            visible_value: "label", 
            tabs: [
                {
                    _id: "main",
                    title: "%Data%",
                    description: "Size, font style, color, and other Typographics settings can be adjusted in the Typographics tab."
                },
                {
                    _id: "typo",
                    title: "Typographics"
                },
                {
                    _id: "style",
                    title: "Stylize"
                },
                {
                    _id: "position",
                    title: "Position"
                },  
                {
                    _id: "behovior",
                    title: "Behovior"
                }, 
            ],
            apollo_fields: { 
                i: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Order", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                label: {
                    type: SCALAR_TYPES.HTML,
                    title: "Label text", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                url: {
                    type: SCALAR_TYPES.STRING,
                    title: "URL", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                design_type: {
                    type: SCALAR_TYPES.RADIO,
                    values: Object.values( LINKS_DESIGN_TYPE ).map(v => ({_id:v, title: v})),
                    title: "Design type",  
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, 
                image: {
                    type: SCALAR_TYPES.MEDIA,
                    title: "Image", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                imageHeight: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Image height", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                imageWidth: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Image width", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                icon: {
                    type: SCALAR_TYPES.ICON,
                    title: "Icon", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                button_color: {
                    type: SCALAR_TYPES.COLOR,
                    title: "Button color", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                border_width: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Border width", 
                    required: false,
                    thread: false,
                    editable: true,
                    default: 1,
                    tab: "main",
                },
                labelStyle:{
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Label CSS style", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }
            },
            module: "pe-landing-module",

        },

        DotNavidationItem: {
            interface: "DotNavigation", 
            name: "Dot of vertical navigation",
            plural: "Dots of vertical navi",
            visible_value: "title",
            isNotCard: true,
            icon: __dot_navidation,
            tabs: [
                {
                    _id: "main",
                    title: ''
                },
                {
                    _id: "style",
                    title: "Style"
                }
            ],
            apollo_fields: {
                anchor:  {
                    type: SCALAR_TYPES.STRING,
                    title: "Ancor",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                title:  {
                    type: SCALAR_TYPES.STRING,
                    title: "Title",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                isHidden:  {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Is hidden",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
            },
            module: "pe-landing-module",
        },
        [LANDING_SCALAR.MARRIAGE_ITEM]: {
            interface: "MarriageItem", 
            name: "Image item",
            plural: "Images items",
            visible_value: "title",
            isNotCard: true,
            icon: __image,
            tabs: [
                {
                    _id: "main",
                    title: ''
                },
                {
                    _id: "style",
                    title: "Style"
                }
            ],
            apollo_fields: {
                src: {
                    type: SCALAR_TYPES.MEDIA,
                    subtitle: "Image", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, // URL
                link_url: {
                    type: SCALAR_TYPES.STRING,
                    subtitle: "Link URL", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                link_target: {
                    type: SCALAR_TYPES.TAGS,
                    subtitle: "Link target",
                    values: [
                        {
                            _id: "_blank",
                            title: "Over window",
                        },
                        {
                            _id: "_self",
                            title: "This window",
                        },
                    ],
                    default:"_blank",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true,
                    tab: "main",
                },
                style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Style",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "style",
                },
                border: {
                    type: SCALAR_TYPES.BORDER,
                    subtitle: "Image's border",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true,
                    tab: "style",
                }
            }
        },
        
        [ LANDING_SCALAR.FIELD_INPUT_ITEM ]: {
            interface: "ContactFormField", 
            name: "Field Input Item",
            plural: "Field Input Items",
            visible_value: "title",
            isNotCard: true,
            icon: __card,
            tabs: [
                {
                    _id: "main",
                    title: ''
                },
                {
                    _id: "style",
                    title: "Style"
                },
                {
                    _id: "presets",
                    title: "Presets"
                },
                {
                    _id: "composition",
                    title: "Composition"
                },
            ],
            apollo_fields: {
                field: {
                    type: SCALAR_TYPES.STRING,
                    subtitle: "Field uniq", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                type: {
                    type: SCALAR_TYPES.TAGS,
                    subtitle: "Type", 
                    values: Object.keys(SCALAR_TYPES).map( val => ({
                        _id: val,
                        title: __( val ),
                    })),
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                min: {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "Minimal value",  
                    required: ["type", SCALAR_TYPES.NUMBER, SCALAR_TYPES.SLIDER],
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                max: {
                    type: SCALAR_TYPES.NUMBER,
                    subtitle: "Maximal value",  
                    required: ["type", SCALAR_TYPES.NUMBER, SCALAR_TYPES.SLIDER],
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                required: {
                    type: SCALAR_TYPES.BOOLEAN,
                    subtitle: "Is require?",  
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
            }
        },
        Card: {
            interface: "ICard", 
            name: "Card",
            plural: "Cards",
            visible_value: "title",
            isNotCard: true,
            icon: __card,
            tabs: [
                {
                    _id: "main",
                    title: ''
                },
                {
                    _id: "style",
                    title: "Style"
                },
                {
                    _id: "presets",
                    title: "Presets"
                },
                {
                    _id: "composition",
                    title: "Composition"
                },
            ],
            apollo_fields: {
                back_color: {
                    type: SCALAR_TYPES.COLOR,
                    subtitle: "Back color", 
                    required: false,
                    thread: false,
                    notClear: true,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, //ID 
                orientation:  {
                    isHidden: true
                }, //VerticalType 
                cardFields:  {
                    isHidden: true
                }, //CardFiled[]
                cards_count:  {
                    isHidden: true
                }, //number
                cards_height:  {
                    isHidden: true
                }, //number 
                hide_action_icon:  {
                    isHidden: true
                }, //boolean
                decoration:  {
                    isHidden: true
                }, //any
                clipping:  {
                    isHidden: true
                }, //boolean
                vertical_align:  {
                    isHidden: true
                }, //ALIGN
                card_style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Card CSS style", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                }, //CSSProperties
                card_inner_style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Card inner CSS style", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                }, //CSSProperties
                tail_type:  {
                    isHidden: true
                }, //CARD_TAILE_TYPE
                taIl_style:  {
                    isHidden: true
                }, //CSSProperties
                tail_color_hover:  {
                    isHidden: true
                }, //any
                is_empty_tail_hide: {
                    isHidden: true
                }, //boolean
                tension:  {
                    isHidden: true
                }, //any
                border_radius:  {
                    
                    tab: "style",
                }, //number | number[]
                box_shadow:  {
                    isHidden: true
                }, //string
                random_rotaion:  {
                    isHidden: true
                }, //number
                horizontal_grid_proportia:  {
                    isHidden: true
                }, //number[]
                reverse_mounting:  {
                    isHidden: true
                }, //boolean
                is_back_image:  {
                    isHidden: true
                }, //boolean
                is_contrast_muar:  {
                    isHidden: true
                }, //boolean
                back_image_vertical:  {
                    isHidden: true
                }, //string|number
                value:  {
                    isHidden: true
                }, //any[]
                back_image: {
                    
                    tab: "style",
                }, // URL|string 
                is_joint:  {
                    isHidden: true
                }, //boolean
                back_opacity: {
                    isHidden: true
                }, //number
                color:  {
                    isHidden: true
                }, //Color
                lasy_load_behavior:  {
                    isHidden: true
                }, //LASY_LOAD_BEHAVIOR_TYPE
                style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "CSS style", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                }, //CSSProperties
                className:  {
                    type: SCALAR_TYPES.STRING,
                    title: "Card's css class names", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                }, //string    
                animated: {
                    type: LANDING_SCALAR.ANIMATED, 
                    title: "Animation", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                before: {
                    type: SCALAR_TYPES.HTML,
                    title: "HTML before card", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "presets",
                }, // HTML
                after:  {
                    type: SCALAR_TYPES.HTML,
                    title: "HTML after card", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "presets",
                }, //HTML
                cardTitle:  {
                    type: SCALAR_TYPES.HTML,
                    title: "Extend card title", 
                    description: "",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "presets",
                }, //HTML
                cardDescription:  {
                    type: SCALAR_TYPES.HTML,
                    title: "Extend card title", 
                    description: "",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "presets",
                }, //HTML
                cardIcon:  {
                    type: SCALAR_TYPES.ICON,
                    title: "Extend card icon", 
                    description: "",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "presets",
                }, //Icon
                cardColor:  {
                    type: SCALAR_TYPES.COLOR,
                    title: "Extend card color", 
                    description: "",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "presets",
                }, //Color
                borderType:  {
                    type: SCALAR_TYPES.TAGS, 
                    title: "Border type",
                    values : Object.values( BORDER_TYPE ).map(v => ({ _id:v, title: __( v ) })),
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                }, //BORDER_TYPE
                id: {
                    type: SCALAR_TYPES.ID,
                    subtitle: "id", 
                    required: false,
                    thread: false,
                    editable: false,
                    tab: "main",
                }, //ID 
                isHidden: {
                    type: SCALAR_TYPES.BOOLEAN,
                    subtitle: "Do hide", 
                    required: false,
                    thread: false,
                    notClear: true,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, //ID 
            }
        },
        rectangle: {
            name: "rectangle",
            plural: "rectangles",
            parentType: "Primitive",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                type: {
                    isHidden: true
                }
            }
        }, 
        oval: {
            name: "oval",
            plural: "ovals",
            parentType: "Primitive",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                type: {
                    isHidden: true
                }
            }
        }, 
        star: {
            name: "star",
            plural: "stars",
            parentType: "Primitive",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                type: {
                    isHidden: true
                }
            }
        }, 
        heart: {
            name: "heart",
            plural: "hearts",
            parentType: "Primitive",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                type: {
                    isHidden: true
                }
            }
        }, 
        order: {
            name: "order",
            plural: "orders",
            parentType: "Primitive",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                type: {
                    isHidden: true
                }
            }
        }, 
        line: {
            name: "line",
            plural: "lines",
            parentType: "Primitive",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                type: {
                    isHidden: true
                }
            }
        }, 
        curve: {
            name: "curve",
            plural: "curves",
            parentType: "Primitive",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                type: {
                    isHidden: true
                }
            }
        }, 
        Border: {
            interface: "CSSBorder", 
            name: "Dot of vertical navigation",
            plural: "Dots of vertical navi",
            visible_value: "title",
            isNotCard: true,
            icon: __dot_navidation,
            tabs: [
                {
                    _id: "main",
                    title: ''
                }
            ],
            apollo_fields: {

            }
        },
        TimelineDot: {
            interface: "Timeline", 
            name: "Timeline",
            plural: "Timeline",
            visible_value: "title",
            isNotCard: true,
            tabs: [
                {
                    _id: "main",
                    title: ''
                },
                {
                    _id: "style",
                    title: "Stylize"
                }
            ],
            apollo_fields: {

                type:  {
                    type: SCALAR_TYPES.IMAGE_RADIO,
                        title: "Text align", 
                        values: Object.values(TIMELINE_DIAGRAM_TYPE).map(key => ({
                            _id: key,
                            img: `/assets/img/landing/align-${key}.svg`,
                            height: 25,
                            title: undefined
                        })),
                        required: false,
                        thread: false,
                        editable: true,
                        default: TIMELINE_DIAGRAM_TYPE.SINUSOID,
                        noDefault: false,
                        tab: "main",
                }, //?: TIMELINE_DIAGRAM_TYPE
                color:  {
                    type: SCALAR_TYPES.COLOR,
                    title: "color",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?:  Color
                perimeter:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "perimeter (?)",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: number
                weight:  {
                    type: SCALAR_TYPES.SLIDER,
                    title: "weight",
                    min:0,
                    max:250,
                    stepSize: 1,
                    labelStepSize: 50,
                    required: false,
                    thread: false,
                    editable: true,
                    default: 20,
                    tab: "main",
                }, //?: number
                fontSize:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "font Size",
                    required: false,
                    thread: false,
                    editable: true,
                    default: 14,
                    tab: "main",
                }, //?: number
                fontColor:  {
                    type: SCALAR_TYPES.COLOR,
                    title: "font Color",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: Color
                radius:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "radius",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: number | string
                significant_zero:  {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "significant zero",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?:boolean
                shift:  {
                    type: SCALAR_TYPES.SLIDER,
                    title: "Shift of start wave",
                    min:0,
                    max:1,
                    stepSize: 0.1,
                    labelStepSize: 0.25,
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: number
                length:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Length of wave",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: number
                amplitude:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Amplitude",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: number  // 0-110
                stroke_width:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Stroke width",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: number
                svg_style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "SVG path",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },//?: CSSProperties
                dot_style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Dot",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },//?: CSSProperties
                dot_before_style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Dot (:before)",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },//?: CSSProperties
                dot_after_style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Dots (:after)",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },//?: CSSProperties
                isSlide:  {
                    type: SCALAR_TYPES.BOOLEAN,
                    title: "Показывать в карусели. Не реализовано",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: boolean //Показывать в карусели. Не реализовано
                slidesPerScreen:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Slides per screen",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: number
                dot_stroke_width:  {
                    type: SCALAR_TYPES.NUMBER,
                    title: "dot stroke width",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, //?: number
            },
        },
        MenuItem: {
            interface: "IMenuItem", 
            name: "Menu item",
            plural: "Menu items",
            visible_value: "title",
            isNotCard: true,
            icon: __dot_navidation,
            tabs: [
                {
                    _id: "main",
                    title: ''
                },
                {
                    _id: "style",
                    title: "Stylize"
                }, 
                {
                    _id: "behovior",
                    title: "Behovior"
                }, 
            ],
            apollo_fields: {
                icon: {
                    type: SCALAR_TYPES.ICON,
                    title: "Icon", 
                    description: "",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                title:  {
                    type: SCALAR_TYPES.STRING,
                    title: "Title",
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                route: {
                    type: SCALAR_TYPES.STRING,
                    title: "Link URL", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                },
                children : {
                    type: "external_array",
                    kind: "sections",
                    component: "MenuItem",
                    title: "Menu Items",
                    dataSection: "Menu Items", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                className:  {
                    type: SCALAR_TYPES.STRING,
                    title: "Landing's CSS classes",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                style:  {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    title: "Landing's CSS style",
                    icon: __css,
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "style",
                },
                animated: {
                    type: LANDING_SCALAR.ANIMATED, 
                    title: "Animation", 
                    visualization: "landingCompositeDataType",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "behovior",
                }, 
            }
        }, 
        ContactItem: {
            interface: "IMenuItem", 
            name: "Contact item",
            plural: "Contact items",
            visible_value: "sc",
            isNotCard: true,
            icon: __dot_navidation,
            tabs: [
                {
                    _id: "main",
                    title: 'Main'
                }
            ],
            apollo_fields: {
                sc: {
                    type: SCALAR_TYPES.TAGS,
                    title: "sc",
                    values : Object.values( SC ).map(v => ({ _id:v, title: __( v ) })),
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",   
                }, //SC 
                label: {
                    type: SCALAR_TYPES.HTML,
                    title: "Label", 
                    required: false,
                    thread: false,
                    placeholder: "Start write",
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                url: {
                    type: SCALAR_TYPES.STRING,
                    title: "URL", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, 
                image: {
                    type: SCALAR_TYPES.MEDIA,
                    title: "Image", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                imageHeight: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Image height", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },
                imageWidth: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Image width", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                },  
                style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "CSS style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                }, //?: CSSProperties
                className: {
                    type: SCALAR_TYPES.STRING,
                    subtitle: "CSS classes", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    tab: "main",
                }, //?: string  
            }
        }, 
        Typographics: {
            tabs: [
                {
                    _id: "main",
                    title: 'Main'
                }
            ],
            apollo_fields: {
                font_size: {
                    type: SCALAR_TYPES.NUMBER,
                    title: "Font size",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    default: 14,
                    tab: "main",
                },
                text_color: {
                    type: SCALAR_TYPES.COLOR,
                    title: "Color",
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: true,
                    default: "var(--force-contast-color)",
                    tab: "main",
                },
                text_align: {
                    type: SCALAR_TYPES.IMAGE_RADIO,
                    title: "Text align", 
                    values: Object.values(ALIGN).map(key => ({
                        _id: key,
                        img: `/assets/img/landing/align-${key}.svg`,
                        height: 25,
                        title: undefined
                    })),
                    required: false,
                    thread: false,
                    editable: true,
                    default: ALIGN.START,
                    noDefault: false,
                    tab: "main",
                },
                font_weight: {
                    type: SCALAR_TYPES.TAGS,
                    values: Object.values(FONT_WEIGHT).map((key) => ({
                        _id: key, 
                        title:__( Object.keys(FONT_WEIGHT).find((k) => FONT_WEIGHT[k as keyof typeof FONT_WEIGHT] === key )! ), 
                        style:{ fontWeight: key }
                    })),
                    title: "Font weight", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "main",
                },
                font_case: {
                    type: SCALAR_TYPES.TAGS,
                    values: Object.values(FONT_CASE).map(key => ({ _id:key, title:__( key ), style:{ textTransform: key } })),
                    title: "Font case", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "main",
                },
                font_style: {
                    type: SCALAR_TYPES.TAGS,
                    values: Object.values(FONT_STYLE).map(key => ({ _id:key, title:__( key ), style:{ textTransform: key } })),
                    title: "Font style", 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "main",
                }, 
                line_height: {
                    type: SCALAR_TYPES.SLIDER, 
                    title: "Line height",
                    min:0,
                    max:5,
                    stepSize: 0.1,
                    labelStepSize: 2.5, 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "main",
                },
                letter_spacing: {
                    type: SCALAR_TYPES.SLIDER, 
                    title: "Letter spacing",
                    min:0,
                    max:10,
                    stepSize:1,
                    labelStepSize: 5, 
                    required: false,
                    thread: false,
                    editable: true,
                    noDefault: false,
                    tab: "main",
                }, 
            },
            interface: "ITypographics", 
            name: "Typographics",
            plural: "Typographies",
            visible_value: "font_size",
            isNotCard: true,
        },
        TabDesignStyle: {
            tabs: [
                {
                    _id: "main",
                    title: 'Main'
                }
            ],
            apollo_fields: {
                tab_list_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Tabs list style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                tab_list_before_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Tabs list style (:before)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                tab_list_after_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Tabs list style (:after)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                tab_panel_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Content panel style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                tab_panel_before_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Content panel style (:before)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                tab_panel_after_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Content panel style (:after)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                tab_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Tab button style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                tab_before_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Tab button style (:before)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                tab_after_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Tab button style (:after)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                active_tab_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Active tab button style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                active_tab_before_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Active tab button style (:before)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                active_tab_after_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Active tab button style (:after)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
                tab_indicator_style: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Animated indicator style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main", 
                },
            }
        },
        HAccordeonStyle: {
            tabs: [
                {
                    _id: "main",
                    title: 'Horizontal accordeon'
                },
                {
                    _id: "style",
                    title: 'Stylize'
                },
            ],
            apollo_fields: {
                cover_width: {
                    type: SCALAR_TYPES.SLIDER,
                    subtitle: "Cover width",
                    min: 45,
                    max: 125,
                    stepSize: 1,
                    labelStepSize: 40,
                    default: 100,
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: false, 
                    tab: "main", 
                },
                label_size: {
                    type: SCALAR_TYPES.SLIDER,
                    subtitle: "Label text size",
                    min: 10,
                    max: 50,
                    stepSize: 1,
                    labelStepSize: 10,
                    default: 18,
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: false, 
                    tab: "main", 
                },
                icon_size: {
                    type: SCALAR_TYPES.SLIDER,
                    subtitle: "Icon size",
                    min: 10,
                    max: 50,
                    stepSize: 1,
                    labelStepSize: 10,
                    default: 32,
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: false, 
                    tab: "main", 
                },
                isOrder : {
                    type: SCALAR_TYPES.BOOLEAN,
                    subtitle: "Set order not label",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: false, 
                    tab: "main",
                },
                isNoBackground : {
                    type: SCALAR_TYPES.BOOLEAN,
                    subtitle: "Force ignore background color",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: false, 
                    tab: "main",
                },
                cover_colors: {
                    type: SCALAR_TYPES.COLOR,
                    subtitle: "Covers color or colors range",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "main",
                },
                container: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Container CSS style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                card: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Cover style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                card_open: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Active cover style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                card_cover: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Cover inner style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                card_cover_before: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Cover inner style  (:before)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                card_cover_after: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Cover inner style (:after)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                card_label: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Label style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                card_icon: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Icon style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                content_container: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Content container style",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                content_container_before: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Content container style (:before)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
                content_container_after: {
                    type: SCALAR_TYPES.CSS_PROPERTIES,
                    subtitle: "Content container style (:after)",
                    required: false,
                    thread: false,
                    editable: true, 
                    noDefault: true, 
                    tab: "style", 
                },
            }
        },
        ExternalLink: {
            name: "External link",
            plural: "External links", 
            visible_value: "title",
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                }, 
                {
                    _id: "style",
                    title: "Stylize",
                },
                {
                    _id: "position",
                    title: "Position",
                },
                {
                    _id: "behovior",
                    title: "Behovior",
                },
            ],
            apollo_fields: {
                url: {
                    type: SCALAR_TYPES.STRING,
                    title: "URL", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, // URL
                title: {
                    type: SCALAR_TYPES.STRING,
                    title: "Label", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, // ?: HTML
                description: {
                    type: SCALAR_TYPES.HTML,
                    title: "Description", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, // ?: HTML
                image: {
                    type: SCALAR_TYPES.MEDIA,
                    title: "Image", 
                    required: false,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, // ?: string
                status: {
                    type: SCALAR_TYPES.SELECT,
                    values: Object.values( POST_STATUS ).map(v => ({_id:v, title: v})),
                    title: "Publish status",  
                    required: false,
                    thread: false,
                    editable: true,
                    default:"publish",
                    tab: "main",
                },
                categoryId: {
                    type: LANDING_SCALAR.CATEGORY_ID, 
                    subtitle: "Categories",
                    dataSection: "Section", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    editable: true,
                    tab: "main",
                }, // ?: string
                categories: {
                    type: "external_array",
                    kind: LANDING_SCALAR.CATEGORY,
                    component: LANDING_SCALAR.CATEGORY,
                    subtitle: "Categories",
                    dataSection: "Section", 
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: false,
                    editable: true,
                    tab: "main--",
                }, // ?: string | string[]
                rating: {  
                    subtitle: "Average rating",
                    type: LANDING_SCALAR.RATING,
                    component: LANDING_SCALAR.RATING,
                    visualization: "landingCompositeDataType",
                    required: true,
                    thread: true,
                    editable: false,
                    tab: "main",
                }
            }
        },
        "PERating": {
            tabs: [
                {
                    _id: "main",
                    title: "Main",
                },
            ], 
            "apollo_fields": {
                "id": {
                    "type": "id",
                    "title": "id",
                    "thread": 1,
                    "editable": 0
                }, 
                "user": { 
                    "kind": "type",
                    "type": "external",
                    "component": "User",
                    "title": "User",
                    "external_fields": [
                        "id",
                        "display_name",
                        "avatar",
                        "is_blocked"
                    ],
                    "single": 1,
                    "thread": 1,
                    "editable": 1 
                },
                "rating": {
                    "kind": "type",
                    "type": "external",
                    "component": "PERating",
                    "title": "Ratings",
                    "external_fields": [
                        "url",
                        "user",
                        "rating"
                    ],
                    "single": 1,
                    "thread": 1,
                    "editable": 1 
                },
                "post_date": {
                    "type": "date",
                    "title": "Date",
                    "filter_type": "meta",
                    "thread": 0,
                    "editable": 1
                }
            },
            "visibled_value": "rating",
            "name": "Rating",
            "plural": "Ratings",
            "module": "pe-basic-module"
        },
    }
    return schemas
}

export const getLandingContentSchema = ( v: any ): any => {
    const apollo_fields = LandingContentSchema()[ v ] || {} 
    delete apollo_fields.tabs
    delete apollo_fields.visible_value
    delete apollo_fields.parentType2
    return apollo_fields
}

export const getLandingContentTabs = () : any[] => {
    return [
        {
            _id: "main",
            title: "%Data%"
        }, 
        {
            _id: "style",
            title: "Stylize"
        },
        {
            _id: "position",
            title: "Position"
        },  
        {
            _id: "behovior",
            title: "Behovior"
        }, 
        {
            _id: "typo",
            title: "Typographics"
        }, 
        {
            _id: "background",
            title: "Smart background"
        }, 
    ]
}

export const getFullSingleContentSchema = (v: string, _schema: any = undefined): any => {
    _schema                     = !!_schema ? _schema : schema()
    const schemaData            = getSingleLandingContentSchema( v )
    let schemaType              = schemaData.apollo_fields 
    const parentTypeName        = schemaData.parentType
    const parentType            = _schema[ parentTypeName ]?.apollo_fields || {} 
    const parentType2           = _schema[ schemaData.parentType2 ]?.apollo_fields || {} 
    return {
        ...schemaData,
        apollo_fields: { 
            ...schemaType, 
            parentType: parentTypeName, 
            type: v, 
            ...parentType2, 
            ...parentType, 
        } 
    }
}
export const getSingleLandingContentSchema = (v: string): any => {
    const tabs = LandingContentSchema()[ v ]?.tabs || []
    const data =  { 
        interface: components()[ v ].interface, 
        name:  components()[ v ].title,
        icon: components()[ v ].icon,
        plural: getPlural( components()[ v ].title ),
        parentType: "LandingContent", 
        parentType2: LandingContentSchema()[ v ]?.parentType2 ? LandingContentSchema()[ v ]?.parentType2 : "",
        tabs: [ 
            ...getLandingContentTabs(),  
            ...tabs,
        ],
        apollo_fields: { 
            ...( getLandingContentSchema( v ) ),
            clickId: { 
                type: LANDING_SCALAR.MODAL,
                visualization: "landingCompositeDataType", 
                title: "Do on click",  
                required: false,
                thread: false,
                editable: true,
                noDefault: true,
                tab: "behovior"
            },
            type: {
                isHidden: true
            }
        },
        visible_value: (LandingContentSchema()[ v ] || {}).visible_value, 
        module: "pe-landing-module",
    }  
    return data
}